// src/tabs/ParamsPanel.jsx — onglet Paramètres (admin only).
//
// 5 sous-onglets — réglages SYSTÈME uniquement (Phase 2.A : la fiche logement
// détaillée est passée dans l'onglet Logements / src/tabs/LogementsPanel.jsx,
// cf. ADR-022) :
// - Général : société (nom/SIRET/adresse/email/tel) + RIB + TVA + numéro facture
// - Consommables : produits & prix (2 niveaux Achat / Distribution)
// - Comptes : ComptesPanel (gestion comptes ménage admin)
// - Beds24 : sync state + bookings sans mapping + assignation rapide
// - IA : config Claude API + état des automatisations (lecture — le démarchage
//   se travaille dans Logements › À démarcher, lot 4 opérateur B)
//
// Aucune logique IA inline — tout est délégué aux sous-panels extraits.
//
// DA (CHANTIER Paramètres) : patron §8 ter « fiche + accordéons » — sous-onglets
// icône lucide + actif souligné corail, sections en cartes/accordéons blanc franc
// avec pastille teinte-catégorie (config = ardoise). Habillage SEUL : zéro logique,
// zéro donnée, zéro persistance touchée (société/RIB/TVA/compteurs/footer intacts).

import { useState, useEffect, useRef } from "react"
import {
  Settings, ShoppingCart, Users, Cable, Building2, CreditCard,
  Hash, FolderX, Trash2, AlertTriangle, X, Plus, Link2, Globe, DoorOpen, Scale, ClipboardCheck,
} from "lucide-react"
// LOT « champs orphelins » — vue d'audit « Ce qui manque » (lecture seule).
import CompletudePanel from "./CompletudePanel"
// P-2.46 : texte de l'avertissement près de la case « Assujetti TVA ».
import { AVERTISSEMENT_CASE_TVA } from "../lib/tva-garde"
import PrixRemplacements from "../components/PrixRemplacements"
// CHANTIER OTA Lot O2 — carte « Espace voyageur » (matrice de composition
// globale par plateforme + liste des exceptions par logement).
import { JourneyCompositionGlobalCard } from "../components/JourneyCompositionMatrix"
import { getAllLogsFlat } from "../lib/sousloc-helpers"
import { DS } from "../lib/ui"
import { uid } from "../lib/format"
import ComptesPanel from "./ComptesPanel"
import Beds24SyncPanel from "./Beds24SyncPanel"
import useEchapPourFermer from "../lib/useEchapPourFermer"
import { EVENEMENT_NUMERO_EMIS } from "../app/argent/useFacturesPieces"
import {
  SubTabNav, Section, SubSection, FicheAlert, PrimaryButton, SecondaryButton,
  StatusPill, FieldLabel, daInputStyle,
} from "./_paramsUi"

// LOT 2 (2026-08-26) — les HUIT sous-onglets des réglages, dans l'ordre de
// Dimitri : Ce qui manque · Société · Comptes · Produits · Incidents ·
// Contrat · Beds24 · IA. Les IDS NE BOUGENT PAS (liens profonds intacts) :
// « Société » = l'écran Général, « Produits » = l'écran Consommables.
// « Incidents » et « Contrat » n'ont pas encore d'écran : l'entrée existe et
// le dit — textes d'aide = état actuel, jamais une promesse muette.
const PARAM_TABS = [
  { id: "completude",   label: "Ce qui manque", icon: ClipboardCheck },
  { id: "general",      label: "Société",       icon: Settings },
  { id: "comptes",      label: "Comptes",       icon: Users },
  { id: "consommables", label: "Produits",      icon: ShoppingCart },
  { id: "incidents",    label: "Incidents",     icon: ClipboardCheck },
  { id: "contrat",      label: "Contrat",       icon: ClipboardCheck },
  { id: "beds24",       label: "Beds24",        icon: Cable },
]

export default function ParamsPanel({ ongletInitial = "completude", cfg, save, data, saveData, currentUser, callGetInvoiceSequences, callSetInvoiceSequence, callContractsOrphansList, callContractsOrphansDelete }) {
  const [c, setC] = useState({ ...cfg, consommables: cfg.consommables || [] })
  // Re-sync l'état local quand cfg change ailleurs (ex. modif faite dans
  // LogementsPanel pendant que l'utilisateur navigue). Sans ce useEffect,
  // un clic enregistrer dans Paramètres écrase silencieusement les modifs
  // récentes d'autres panels (audit-finding F2).
  useEffect(() => {
    setC({ ...cfg, consommables: cfg.consommables || [] })
  }, [cfg])
  const [paramTab, setParamTab] = useState(ongletInitial)
  // eslint-disable-next-line no-unused-vars
  const [activeLogConso, setActiveLogConso] = useState(cfg.props?.[0]?.logs?.[0]?.id || "")

  // NOTE Phase 2.A : la CRUD propriétaires & logements (sections fiches détaillées)
  // a été déplacée vers le nouvel onglet Logements (cf. ADR-022).
  // ParamsPanel ne gère plus que les réglages SYSTÈME (société, RIB, TVA, numérotation
  // factures, produits & prix de remplacement, comptes utilisateurs, contrat, Beds24).
  // Ni IA ni import CSV global ici : l'onglet IA a déménagé vers Opérateur ›
  // Réglages, et l'import CSV n'a jamais eu d'écran dans ce panneau.
  // L'ajout/suppression de proprio/logement est reporté à Phase 2.A Étape 2 dans
  // LogementsPanel.

  const inp = (val, onChange, placeholder = "") => <input value={val} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={daInputStyle} />

  // Lot B (M-2) : saveCfg passe par /api/cfg (HTTP) → await + toast d'erreur
  // (avant : save(c) fire-and-forget → perte silencieuse + UI "enregistré" faux).
  const [errToast, setErrToast] = useState(null)
  const errTimerRef = useRef(null)
  // Le bandeau porte DEUX natures de message, et c'est voulu : l'échec
  // d'enregistrement, et l'avertissement « enregistré, mais la copie lue par les
  // agents de ménage est restée périmée ». Le second est un SUCCÈS — mais un
  // succès dont la conséquence (des agents sur un ancien code de porte) mérite
  // le même degré d'attention qu'une erreur.
  const afficherMessage = (texte) => {
    if (errTimerRef.current) clearTimeout(errTimerRef.current)
    setErrToast(texte)
    errTimerRef.current = setTimeout(() => setErrToast(null), 7000)
  }
  const saveCfgSafe = async (cfgToSave) => {
    try {
      const avertissement = await save(cfgToSave)
      if (avertissement) afficherMessage(avertissement)
    } catch (e) {
      console.error("[ParamsPanel] saveCfg", e)
      afficherMessage(e.message || "Échec de l'enregistrement")
    }
  }

  const SaveBar = () => (
    <PrimaryButton onClick={() => saveCfgSafe(c)} style={{ width: "100%", padding: 14, fontSize: 14, marginBottom: 20 }}>
      Enregistrer
    </PrimaryButton>
  )

  return (
    <div style={{fontFamily:DS.fontBody}}>
      {errToast && (
        <div role="alert"
          style={{position:"fixed",bottom:24,right:24,zIndex:1003,background:DS.danger,color:"#fff",padding:"11px 14px",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,.25)",display:"flex",alignItems:"center",gap:12,fontFamily:DS.fontBody,fontSize:12,fontWeight:600,maxWidth:340}}>
          <AlertTriangle size={16} strokeWidth={2} style={{flex:"0 0 auto"}} />
          <span style={{flex:1}}>{errToast}</span>
          <button onClick={() => setErrToast(null)} aria-label="Fermer" style={{background:"none",color:"rgba(255,255,255,.7)",border:"none",cursor:"pointer",lineHeight:1,display:"flex"}}><X size={16} /></button>
        </div>
      )}

      {/* Sous-onglets Params — icône lucide + actif souligné corail (§8 ter). */}
      <SubTabNav items={PARAM_TABS} active={paramTab} onSelect={setParamTab} />

      {paramTab === "general" && <>
        <Section tint="ardoise" icon={Building2} title="Informations société" subtitle="Identité, RIB et TVA — figurent sur les factures.">
          {[["Nom", "nom"], ["SIRET", "siret"], ["Adresse", "adresse"], ["Email", "email"], ["Téléphone", "tel"]].map(([label, key]) => (
            <div key={key} style={{marginBottom:12}}>
              <FieldLabel>{label}</FieldLabel>
              {inp(c.co?.[key] || "", v => setC({ ...c, co: { ...c.co, [key]: v } }))}
            </div>
          ))}
          <SubSection icon={CreditCard} title="RIB (affiché sur factures)">
            <div style={{marginBottom:10}}><FieldLabel>IBAN</FieldLabel>{inp(c.co?.iban || "", v => setC({ ...c, co: { ...c.co, iban: v } }))}</div>
            <div><FieldLabel>BIC</FieldLabel>{inp(c.co?.bic || "", v => setC({ ...c, co: { ...c.co, bic: v } }))}</div>
          </SubSection>
          {/* LOT « champs orphelins » — MÉDIATEUR DE LA CONSOMMATION.
              Ces deux champs étaient LUS par le générateur de contrat
              (lib/contrat-html.js:450-451) sans qu'aucune saisie n'existe :
              l'article 16 disparaissait donc du contrat, en silence.
              Le texte d'aide décrit l'état ACTUEL (règle repo), pas un projet. */}
          <SubSection
            icon={Scale}
            title="Médiateur de la consommation"
            intro={
              <>
                Sert à l'<strong>article 16</strong> du contrat de location. Cet article ne
                s'affiche que lorsque le loueur est <strong>FULLBNBZEN SAS</strong> — c'est-à-dire
                sur les contrats des chambres en sous-location (Maison Zen). Pour un propriétaire
                particulier, l'obligation de médiation ne s'applique pas et l'article ne s'affiche
                pas. Les deux champs doivent être remplis : s'il en manque un, l'article disparaît
                du contrat sans avertissement.
              </>
            }
          >
            <div style={{marginBottom:10}}>
              <FieldLabel>Nom du médiateur</FieldLabel>
              {inp(c.co?.mediateur_nom || "", v => setC({ ...c, co: { ...c.co, mediateur_nom: v } }), "ex : CM2C")}
            </div>
            <div>
              <FieldLabel>Coordonnées du médiateur</FieldLabel>
              {inp(c.co?.mediateur_coordonnees || "", v => setC({ ...c, co: { ...c.co, mediateur_coordonnees: v } }), "ex : 14 rue Saint-Jean, 75001 Paris — cm2c.net")}
            </div>
          </SubSection>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 0 2px"}}>
            <input type="checkbox" id="tva" checked={c.co?.tva} onChange={e => setC({ ...c, co: { ...c.co, tva: e.target.checked } })} />
            <label htmlFor="tva" style={{fontSize:13,color:DS.textPrimary}}>Assujetti TVA</label>
            {c.co?.tva && <><input type="number" value={c.co?.tauxTva} onChange={e => setC({ ...c, co: { ...c.co, tauxTva: Number.isFinite(parseFloat(e.target.value)) ? parseFloat(e.target.value) : 20 } })} style={{width:62,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 8px",fontSize:13,outline:"none",background:DS.card,fontFamily:DS.fontBody}} /><span style={{fontSize:13,color:DS.textSecondary}}>%</span></>}
          </div>
          {/* P-2.46 (décision Dimitri du 29/10) : la case ne change aucun montant
              tant que la facturation avec TVA (P-5.35) n'existe pas ; cochée,
              elle BLOQUE l'émission (garde partagé src/lib/tva-garde.js). */}
          <div data-testid="tva-avertissement" style={{fontSize:12,color:DS.textSecondary,padding:"2px 0 0",lineHeight:1.4}}>
            {AVERTISSEMENT_CASE_TVA}
          </div>
          {/* Pass B : l'ancien champ « Dernier numéro de facture » (cfg.lastNum) est
              retiré. La numérotation est gérée par série en base (invoice_sequences,
              RPC séquentiel inviolable CGI 289). Voir la section « Compteurs de
              factures » ci-dessous. */}
          {/* W5 — pied de page livret fusionné en sous-section de cette carte
              société (données toujours cfg.co.footer.*, distinctes de cfg.co.*). */}
          <FooterGlobalSection c={c} setC={setC} inp={inp} />
        </Section>

        <InvoiceCountersSection
          callGetInvoiceSequences={callGetInvoiceSequences}
          callSetInvoiceSequence={callSetInvoiceSequence}
        />

        <FicheAlert tone="info" style={{marginBottom:14}}>
          Toute la configuration par logement (codes accès, Wi-Fi, contact, commission, mapping Beds24, investissement…) est désormais dans l'onglet <strong>Logements</strong> (cf. ADR-022). ParamsPanel garde uniquement les <strong>réglages système</strong> : ce qui manque à remplir, société + RIB/TVA, compteurs de factures, produits &amp; prix de remplacement, comptes utilisateurs, contrat et connexion Beds24. Les réglages de l'IA sont dans <strong>Opérateur › Réglages</strong>.
        </FicheAlert>

        <ContractOrphansSection
          callContractsOrphansList={callContractsOrphansList}
          callContractsOrphansDelete={callContractsOrphansDelete}
        />

        {/* CHANTIER OTA Lot O2 — matrice de composition de l'espace voyageur
            (globale par plateforme ; les exceptions par logement s'éditent dans
            la fiche logement → Info FBZ → Administration). Persistance DB
            autonome (journey_composition_config via beds24-mapping), hors cfg —
            la SaveBar de cette page ne la concerne pas. */}
        <Section
          tint="ardoise"
          icon={DoorOpen}
          title="Espace voyageur"
          subtitle="Blocs de l'espace voyageur (formulaire, caution, livret, reçu) + état des lieux (section du livret d'accueil, module à venir) par plateforme — état effectif Activé/Désactivé, exceptions par logement."
        >
          <JourneyCompositionGlobalCard
            resolveLogName={(lid) => {
              const found = getAllLogsFlat(cfg).find((l) => l.id === lid)
              return (found && (found.nom || found.nom_complet)) || lid
            }}
            onOpenLogement={(lid) => {
              // Lien vers la fiche logement : dépose la cible pour
              // LogementsPanel (lue à son mount) puis bascule d'onglet via
              // l'écouteur App.jsx (fbz:goto-logement).
              try { sessionStorage.setItem("fbz8-goto-logement", lid) } catch {}
              window.dispatchEvent(new CustomEvent("fbz:goto-logement"))
            }}
          />
        </Section>
        <SaveBar />
      </>}

      {/* PIVOT (Dimitri 2026-09-03) — L'ONGLET « IA » A DÉMÉNAGÉ dans la
          destination de l'opérateur (onglet « Réglages »,
          src/views/operateur/OperateurReglages.jsx) : « une SEULE destination
          qui contient tout… l'onglet IA des Réglages disparaît ».
          ⚠️ Il n'y avait AUCUN interrupteur à déménager : le lot 4 opérateur
          avait déjà retiré le bloc prospection, et la clé Claude est côté
          serveur depuis le lot B. Ce qui restait était de la prose.
          ⚠️ L'INVARIANT « aucun champ de saisie de clé Claude dans l'app » a
          SUIVI le contenu : il est désormais vérifié sur la destination
          (tests/e2e/cfg-form-ia-roles.spec.js) — il n'a pas été supprimé avec
          l'onglet. */}

      {paramTab === "consommables" && <>
        {/* ── Prix des remplacements (refonte Incidents 2026-08-13) — le prix
            HABITUEL par type d'article cassé, affiché en lecture seule sur les
            cartes d'incident admin. Table incident_types_prix, RLS admin seul. ── */}
        <Section tint="corail" icon={ShoppingCart} title="Prix des remplacements" subtitle="Prix habituel par type d'article cassé — guide le rachat, ne le contraint pas.">
          <PrixRemplacements />
        </Section>

        {/* ── Tableau produits globaux (refonte 2026-04 : 2 niveaux Achat / Distribution) ── */}
        <Section tint="ardoise" icon={ShoppingCart} title="Produits & prix" subtitle="Niveau ACHAT = ce que tu vois en magasin · DISTRIBUTION = unité de saisie société.">
          <div className="fbz-table-wrap" style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:980}}>
              <thead>
                <tr style={{background:DS.pageBg}}>
                  <th style={chTh}>Produit</th>
                  <th style={chTh}>Catégorie</th>
                  <th colSpan={3} style={{...chTh,textAlign:"center",color:DS.marine,fontWeight:800,borderLeft:`1px solid ${DS.border}`,borderRight:`1px solid ${DS.border}`,background:DS.catArdoiseBg}}>Niveau achat</th>
                  <th colSpan={2} style={{...chTh,textAlign:"center",color:DS.catCielIco,fontWeight:800,background:DS.catCielBg}}>Distribution</th>
                  <th style={chTh}>Prix unité</th>
                  <th style={chTh}>Enseigne</th>
                  <th></th>
                </tr>
                <tr style={{background:DS.pageBg}}>
                  <th></th><th></th>
                  <th style={{...chTh2,borderLeft:`1px solid ${DS.border}`}}>Format</th>
                  <th style={chTh2}>Qty</th>
                  <th style={{...chTh2,borderRight:`1px solid ${DS.border}`}}>Prix</th>
                  <th style={chTh2}>Unité prélev.</th>
                  <th style={chTh2}>Qty prélev.</th>
                  <th></th><th></th><th></th>
                </tr>
              </thead>
              <tbody>
                {(c.consommables || []).map((cs, i) => {
                  const upCs = (key, val) => setC(prev => ({ ...prev, consommables: prev.consommables.map((x, j) => j === i ? { ...x, [key]: val } : x) }))
                  const inp2 = (key, type = "text", w = 120) => (
                    <input type={type} value={cs[key] ?? ""} onChange={e => {
                        // Frappe numérique invalide = REFUSÉE (la valeur précédente reste),
                        // jamais un 0 fantôme écrit en silence.
                        if (type !== "number") return upCs(key, e.target.value)
                        const n = parseFloat(e.target.value)
                        if (Number.isFinite(n)) upCs(key, n)
                      }}
                      style={{border:`0.5px solid ${DS.borderHover}`,borderRadius:7,padding:"4px 8px",width:w,fontSize:11,outline:"none",fontFamily:DS.fontBody,background:DS.card}}/>
                  )
                  const sel = { border:`0.5px solid ${DS.borderHover}`,borderRadius:7,padding:"4px 6px",fontSize:11,outline:"none",fontFamily:DS.fontBody,background:DS.card }
                  const categorie = cs.categorie ?? cs.type ?? 'kit_accueil'
                  const qtyAchat = cs.qtyParAchat ?? cs.contenance ?? 1
                  const qtyPrelev = cs.qtyParPrelevable ?? qtyAchat
                  const prixUnite = qtyAchat > 0 ? (cs.prixAchat || 0) / qtyAchat : 0
                  const prixPrelev = qtyAchat > 0 ? (cs.prixAchat || 0) * (qtyPrelev / qtyAchat) : 0
                  const divisible = qtyPrelev > 0 && qtyAchat % qtyPrelev === 0
                  return (
                    <tr key={cs.id} style={{borderBottom:`1px solid ${DS.border}`}}>
                      <td style={{padding:"6px 8px"}}>{inp2("nom", "text", 130)}</td>
                      <td style={{padding:"6px 8px"}}>
                        <select value={categorie} onChange={e => upCs("categorie", e.target.value)} style={sel}>
                          <option value="kit_accueil">Kit</option>
                          <option value="operationnel">Opérat.</option>
                        </select>
                      </td>
                      <td style={{padding:"6px 8px",borderLeft:`1px solid ${DS.border}`}}>{inp2("formatAchat", "text", 80)}</td>
                      <td style={{padding:"6px 8px"}}>{inp2("qtyParAchat", "number", 55)}</td>
                      <td style={{padding:"6px 8px",borderRight:`1px solid ${DS.border}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:3}}>
                          <input type="number" step="0.001" value={cs.prixAchat ?? 0} onChange={e => {
                              const n = parseFloat(e.target.value)
                              if (Number.isFinite(n)) upCs("prixAchat", n)
                            }}
                            style={{border:`0.5px solid ${DS.borderHover}`,borderRadius:7,padding:"4px 8px",width:70,fontSize:11,outline:"none",fontFamily:DS.fontBody,color:DS.marine,fontWeight:700,background:DS.card}}/>
                          <span style={{fontSize:10,color:DS.textMuted}}>€</span>
                        </div>
                      </td>
                      <td style={{padding:"6px 8px"}}>{inp2("unitePrelevable", "text", 110)}</td>
                      <td style={{padding:"6px 8px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:3}}>
                          {inp2("qtyParPrelevable", "number", 55)}
                          {!divisible && <AlertTriangle size={13} strokeWidth={2} title="qtyParAchat n'est pas divisible par qtyParPrelevable" style={{color:DS.warning,flex:"0 0 auto"}} />}
                        </div>
                        <div style={{fontSize:9,color:DS.catCielIco,marginTop:2,fontWeight:600}}>
                          ≈ {prixPrelev.toFixed(3)} €
                        </div>
                      </td>
                      <td style={{padding:"6px 8px",color:DS.marine,fontWeight:700,fontSize:11}}>{prixUnite.toFixed(3)} €</td>
                      <td style={{padding:"6px 8px"}}>{inp2("enseigne", "text", 90)}</td>
                      <td style={{padding:"6px 8px",textAlign:"center",whiteSpace:"nowrap"}}>
                        <button onClick={() => setC(prev => ({ ...prev, consommables: prev.consommables.filter((_, j) => j !== i) }))}
                          aria-label="Supprimer ce produit" title="Supprimer ce produit"
                          className="fbz-press" style={{background:"none",border:"none",cursor:"pointer",color:DS.textMuted,display:"inline-flex",padding:4}}><Trash2 size={15} strokeWidth={1.8} /></button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <button onClick={() => setC(prev => ({ ...prev, consommables: [...prev.consommables, { id: "cs" + uid(), nom: "Nouveau produit", categorie: "kit_accueil", formatAchat: "paquet", qtyParAchat: 1, prixAchat: 0, unitePrelevable: "unité", qtyParPrelevable: 1, enseigne: "" }] }))}
            className="fbz-press"
            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,width:"100%",padding:10,background:"transparent",color:DS.marine,border:`1px dashed ${DS.borderHover}`,borderRadius:DS.radiusControl,fontSize:12.5,fontWeight:600,cursor:"pointer",marginTop:10,fontFamily:DS.fontBody}}>
            <Plus size={15} strokeWidth={2} /> Ajouter un produit
          </button>
        </Section>
        <SaveBar />
      </>}

      {paramTab === "comptes" && <ComptesPanel cfg={cfg}/>}

      {paramTab === "beds24" && <Beds24SyncPanel cfg={cfg}/>}

      {/* LOT « champs orphelins » — audit LECTURE SEULE. On lui passe `cfg`
          (la config ENREGISTRÉE), pas l'état local `c` : un manque ne doit
          disparaître de cette liste qu'une fois la correction réellement
          persistée, jamais sur une saisie en cours non enregistrée. */}
      {paramTab === "completude" && <CompletudePanel cfg={cfg} save={save} currentUser={currentUser}/>}
      {paramTab === "incidents" && (
        <p style={{fontSize:12.5,color:"#5c6773",lineHeight:1.6,maxWidth:520}}>
          Les prix de remplacement par type d'article se règlent dans
          l'onglet <strong>Produits</strong> › « Prix des remplacements ».
          Il n'y a pas d'autre réglage d'incident pour l'instant.
        </p>
      )}
      {paramTab === "contrat" && (
        <p style={{fontSize:12.5,color:"#5c6773",lineHeight:1.6,maxWidth:520}}>
          Le contrat n'a pas encore d'écran de réglages — ses champs (caution,
          clauses) vivent aujourd'hui dans Société et dans la fiche logement.
          L'écran viendra avec le chantier contrat.
        </p>
      )}
    </div>
  )
}

// En-têtes de la table consommables (DA : gris uppercase fin).
const chTh = {padding:"8px 10px",textAlign:"left",color:DS.textSecondary,fontWeight:700,fontSize:10,textTransform:"uppercase",letterSpacing:.4}
const chTh2 = {padding:"4px 10px",textAlign:"left",color:DS.textMuted,fontSize:9,fontWeight:600}

// ── InvoiceCountersSection (Pass B) ──────────────────────────────────────
//
// Compteurs de factures gérés par série (P/V/AVE…), DYNAMIQUES : le prochain
// numéro suit les factures réellement présentes (il redescend si on supprime).
// Lit les séquences au montage (callGetInvoiceSequences). Édition = caler le
// PROCHAIN numéro voulu → envoie last_number = voulu-1 (modal de confirmation
// chiffrée, pas d'alert). Refus serveur 409 (target_below_present : caler sous
// un numéro encore présent) → message du plancher.
const pad4 = (n) => String(Math.max(0, parseInt(n, 10) || 0)).padStart(4, "0")

export function InvoiceCountersSection({ callGetInvoiceSequences, callSetInvoiceSequence }) {
  const [seqs, setSeqs] = useState(null) // null = chargement, [] = vide
  const [loadErr, setLoadErr] = useState(null)
  const [editing, setEditing] = useState(null) // { prefix, year, wanted } en cours d'édition
  const [confirmRow, setConfirmRow] = useState(null) // { prefix, year, wanted } modal
  // Échap ferme la confirmation — consigne 2026-08-14 (hook partagé).
  useEchapPourFermer(() => setConfirmRow(null), !!confirmRow)
  const [busy, setBusy] = useState(false)
  const [opErr, setOpErr] = useState(null)

  const load = async () => {
    setLoadErr(null)
    try {
      const list = await callGetInvoiceSequences()
      setSeqs(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error("[InvoiceCounters] load", e)
      setLoadErr(e.message || "Chargement impossible")
      setSeqs([])
    }
  }

  useEffect(() => {
    if (typeof callGetInvoiceSequences === "function") load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // P-2.58 (30/10) : relu après chaque émission (crochet commun
  // `fbz:numero-emis`, src/app/argent/useFacturesPieces.js) et au retour sur
  // l'onglet (pièces émises par le départ automatique, côté serveur).
  useEffect(() => {
    if (typeof callGetInvoiceSequences !== "function" || typeof window === "undefined") return undefined
    const relire = () => { load() }
    const auRetour = () => { if (document.visibilityState === "visible") load() }
    window.addEventListener(EVENEMENT_NUMERO_EMIS, relire)
    window.addEventListener("focus", relire)
    document.addEventListener("visibilitychange", auRetour)
    return () => {
      window.removeEventListener(EVENEMENT_NUMERO_EMIS, relire)
      window.removeEventListener("focus", relire)
      document.removeEventListener("visibilitychange", auRetour)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const keyOf = (s) => `${s.prefix}::${s.year}`

  const submit = async () => {
    if (!confirmRow) return
    setBusy(true)
    setOpErr(null)
    try {
      const lastNumber = confirmRow.wanted - 1
      const r = await callSetInvoiceSequence({
        prefix: confirmRow.prefix,
        year: confirmRow.year,
        last_number: lastNumber,
      })
      if (!r.ok) {
        if (r.status === 409 && r.error === "target_below_present") {
          setOpErr(r.message || `Impossible de caler sous un numéro encore présent : minimum ${r.floor}.`)
        } else {
          setOpErr(r.message || r.error || `Échec (http_${r.status}).`)
        }
        setBusy(false)
        return
      }
      setConfirmRow(null)
      setEditing(null)
      await load()
    } catch (e) {
      console.error("[InvoiceCounters] submit", e)
      setOpErr(e.message || "Échec de l'enregistrement")
    }
    setBusy(false)
  }

  return (
    <Section tint="corail" icon={Hash} title="Compteurs de factures" subtitle="Numérotation par série (P/V/AVE…). Le prochain numéro suit les factures présentes.">
      <div style={{fontSize:12.5,color:DS.textSecondary,lineHeight:1.6,marginBottom:14}}>
        Le prochain numéro <strong>suit les factures présentes</strong> : il redescend si vous supprimez des factures. « Caler » force un numéro plus haut (sauter en avant). On ne peut pas caler <strong>sous</strong> un numéro encore présent (CGI&nbsp;289).
      </div>

      {seqs === null && <div style={{fontSize:12,color:DS.textMuted}}>Chargement…</div>}
      {loadErr && <FicheAlert tone="danger" role="alert" style={{marginBottom:8}}>{loadErr}</FicheAlert>}
      {seqs !== null && seqs.length === 0 && !loadErr && (
        <div style={{fontSize:12,color:DS.textMuted}}>Aucune série pour le moment.</div>
      )}

      {seqs !== null && seqs.length > 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {seqs.map((s) => {
            const isEditing = editing && editing.prefix === s.prefix && editing.year === s.year
            return (
              <div key={keyOf(s)} style={{border:`0.5px solid ${DS.border}`,borderRadius:10,padding:"10px 12px",background:DS.pageBg}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:600,fontFamily:DS.fontDisplay,color:DS.marineDark}}>{s.prefix}-{s.year}</div>
                    <div style={{fontSize:11,color:DS.textSecondary,marginTop:2}}>
                      Prochain numéro : <strong style={{color:DS.marine}}>{s.prefix}-{s.year}-{pad4(s.next_number)}</strong>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <StatusPill tone="neutral">max émis {pad4(s.max_emitted)}</StatusPill>
                    {!isEditing && (
                      <SecondaryButton style={{padding:"7px 12px",fontSize:12}} onClick={() => { setOpErr(null); setEditing({ prefix:s.prefix, year:s.year, wanted:String(s.next_number) }) }}>
                        Caler
                      </SecondaryButton>
                    )}
                  </div>
                </div>
                {isEditing && (
                  <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:11,color:DS.textSecondary}}>Caler le prochain numéro à</span>
                    <input type="number" min={1} value={editing.wanted}
                      onChange={(e) => setEditing({ ...editing, wanted: e.target.value })}
                      style={{width:90,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"7px 8px",fontSize:13,outline:"none",background:DS.card,fontFamily:DS.fontBody}} />
                    <span style={{fontSize:11,color:DS.textMuted}}>→ {s.prefix}-{s.year}-{pad4(editing.wanted)}</span>
                    <PrimaryButton
                      disabled={!(parseInt(editing.wanted,10) >= 1)}
                      style={{padding:"7px 14px",fontSize:12}}
                      onClick={() => { setOpErr(null); setConfirmRow({ prefix:s.prefix, year:s.year, wanted: parseInt(editing.wanted,10) }) }}>
                      Valider
                    </PrimaryButton>
                    <button onClick={() => { setEditing(null); setOpErr(null) }}
                      style={{background:"none",color:DS.textSecondary,border:"none",fontSize:12,cursor:"pointer",fontFamily:DS.fontBody}}>
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Modal de confirmation CHIFFRÉE (avance-seulement, irréversible). */}
      {confirmRow && (
        <div style={{position:"fixed",inset:0,background:"rgba(20,30,45,.45)",zIndex:1004,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:DS.card,borderRadius:14,padding:22,maxWidth:420,width:"100%",boxShadow:"0 12px 40px rgba(0,0,0,.3)",fontFamily:DS.fontBody}}>
            <div style={{fontFamily:DS.fontDisplay,fontSize:17,fontWeight:600,color:DS.marineDark,marginBottom:10}}>Confirmer le calage du compteur</div>
            <div style={{fontSize:13,color:DS.textPrimary,lineHeight:1.6,marginBottom:8}}>
              Le prochain numéro deviendra <strong style={{color:DS.marine}}>{confirmRow.prefix}-{confirmRow.year}-{pad4(confirmRow.wanted)}</strong>.
            </div>
            <div style={{fontSize:12,color:DS.textSecondary,lineHeight:1.5,marginBottom:14}}>
              Caler force le prochain numéro émis. Reculer sous un numéro <strong>encore présent</strong> est refusé (collision). Supprimer des factures libère leurs numéros.
            </div>
            {opErr && <FicheAlert tone="danger" role="alert" style={{marginBottom:12}}>{opErr}</FicheAlert>}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <SecondaryButton disabled={busy} onClick={() => { setConfirmRow(null); setOpErr(null) }}>Annuler</SecondaryButton>
              <PrimaryButton disabled={busy} onClick={submit}>{busy ? "Enregistrement…" : "Confirmer"}</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Section>
  )
}

// ── ContractOrphansSection (DETTE 9) ─────────────────────────────────────
// Nettoyage des PDF de contrats orphelins (bucket privé `contracts`). Un dossier
// est orphelin quand sa direct_booking_requests a été purgée. La garde DURE vit
// CÔTÉ SERVEUR (re-check DBR avant tout remove) ; cette UI ne fait que lister et
// déclencher. Suppression PAR DOSSIER, modale de confirmation OBLIGATOIRE.
function formatBytes(n) {
  if (typeof n !== "number" || !isFinite(n) || n < 0) return "—"
  if (n < 1024) return `${n} o`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`
}

function ContractOrphansSection({ callContractsOrphansList, callContractsOrphansDelete }) {
  const [orphans, setOrphans] = useState(null) // null = jamais cherché, [] = vide
  const [loading, setLoading] = useState(false)
  const [loadErr, setLoadErr] = useState(null)
  const [confirmRow, setConfirmRow] = useState(null) // { request_id, file_count } modal
  const [busy, setBusy] = useState(false)
  const [opErr, setOpErr] = useState(null)

  const search = async () => {
    setLoadErr(null)
    setOpErr(null)
    setLoading(true)
    try {
      const list = await callContractsOrphansList()
      setOrphans(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error("[ContractOrphans] list", e)
      setLoadErr(e.message || "Recherche impossible")
      setOrphans([])
    } finally {
      setLoading(false)
    }
  }

  const confirmDelete = async () => {
    if (!confirmRow) return
    setBusy(true)
    setOpErr(null)
    try {
      const r = await callContractsOrphansDelete(confirmRow.request_id)
      if (!r.ok) {
        // 409 not_orphan = la résa existe (la garde serveur a bloqué) → retirer le
        // dossier de la liste et signaler (l'UI était désynchronisée de la base).
        if (r.status === 409 && r.error === "not_orphan") {
          setOpErr("Ce contrat est lié à une réservation existante — suppression refusée.")
        } else {
          setOpErr(r.error || `Erreur (http_${r.status})`)
        }
        setOrphans((prev) => (prev || []).filter((o) => o.request_id !== confirmRow.request_id))
        setConfirmRow(null)
        return
      }
      setOrphans((prev) => (prev || []).filter((o) => o.request_id !== confirmRow.request_id))
      setConfirmRow(null)
    } catch (e) {
      console.error("[ContractOrphans] delete", e)
      setOpErr(e.message || "Suppression impossible")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Section tint="ardoise" icon={FolderX} title="Contrats orphelins" subtitle="PDF de contrats signés dont la réservation a été supprimée.">
      <div style={{fontSize:12,color:DS.textSecondary,marginBottom:14,lineHeight:1.6}}>
        Les contrats liés à une réservation existante ne sont <strong>jamais</strong> supprimables (document légal).
      </div>

      <SecondaryButton disabled={loading} onClick={search} style={{fontSize:12.5}}>
        {loading ? "Recherche…" : "Rechercher les contrats orphelins"}
      </SecondaryButton>

      {loadErr && <FicheAlert tone="danger" role="alert" style={{marginTop:12}}>{loadErr}</FicheAlert>}
      {opErr && <FicheAlert tone="danger" role="alert" style={{marginTop:12}}>{opErr}</FicheAlert>}

      {orphans !== null && !loading && orphans.length === 0 && !loadErr && (
        <FicheAlert tone="success" style={{marginTop:14}}>Aucun contrat orphelin</FicheAlert>
      )}

      {orphans !== null && orphans.length > 0 && (
        <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:10}}>
          {orphans.map((o) => {
            const totalSize = (o.files || []).reduce((s, f) => s + (typeof f.size === "number" ? f.size : 0), 0)
            const firstDate = (o.files || []).map((f) => f.created_at).filter(Boolean).sort()[0] || null
            return (
              <div key={o.request_id} className="fbz-rowhover" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"10px 12px",background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:10}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:700,color:DS.marine,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis"}}>{o.request_id}</div>
                  <div style={{fontSize:11,color:DS.textMuted,marginTop:2}}>
                    {o.file_count ?? "?"} fichier{o.file_count > 1 ? "s" : ""} · {formatBytes(totalSize)}
                    {firstDate && !Number.isNaN(new Date(firstDate).getTime())
                      ? ` · ${new Date(firstDate).toLocaleDateString("fr-FR")}` : ""}
                  </div>
                </div>
                <SecondaryButton tone="danger" style={{flexShrink:0,padding:"7px 12px",fontSize:11.5}}
                  onClick={() => setConfirmRow({ request_id: o.request_id, file_count: o.file_count })}>
                  <Trash2 size={14} strokeWidth={1.9} /> Supprimer
                </SecondaryButton>
              </div>
            )
          })}
        </div>
      )}

      {confirmRow && (
        <div role="dialog" aria-modal="true"
          style={{position:"fixed",inset:0,zIndex:1004,background:"rgba(20,30,45,.45)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:DS.card,borderRadius:14,padding:24,maxWidth:420,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",fontFamily:DS.fontBody}}>
            <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:DS.fontDisplay,fontSize:17,fontWeight:600,color:DS.dangerText,marginBottom:10}}>
              <AlertTriangle size={18} strokeWidth={2} /> Suppression définitive
            </div>
            <div style={{fontSize:13,color:DS.textPrimary,lineHeight:1.6,marginBottom:8}}>
              Ce contrat n'est lié à <strong>aucune réservation existante</strong>.
              Les {confirmRow.file_count ?? "?"} fichier{confirmRow.file_count > 1 ? "s" : ""} du dossier seront <strong>définitivement supprimés</strong>.
            </div>
            <div style={{fontSize:11,color:DS.textMuted,fontFamily:"monospace",marginBottom:18,wordBreak:"break-all"}}>{confirmRow.request_id}</div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <SecondaryButton disabled={busy} onClick={() => { if (!busy) setConfirmRow(null) }}>Annuler</SecondaryButton>
              <PrimaryButton disabled={busy} onClick={confirmDelete} style={{background: busy ? "#cdd5df" : DS.danger}}>
                {busy ? "Suppression…" : "Supprimer définitivement"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Section>
  )
}

// ── FooterGlobalSection (BUG 34) ─────────────────────────────────────────
//
// Config GLOBALE du pied de page des livrets voyageur, stockée dans
// cfg.co.footer.* (blob fbz8-cfg) et exposée au payload voyageur (footer_config)
// puis rendue par src/LivretFooter.jsx. Édité une seule fois pour toute la SAS.
//
// Hoguet : les mentions légales doivent rester en vocabulaire « conciergerie /
// mise en relation / accompagnement » — jamais « gestion / gérer / mandat ».
//
// Sauvegarde : via le bouton Enregistrer de l'onglet Général (saveCfgSafe(c)),
// pas de bouton dédié — la config footer est un sous-objet de cfg.co.
//
// W5 (fusion UI) : rendu comme SOUS-SECTION de la carte « Informations société »
// (regroupement visuel demandé), séparé par un divider. Les données restent
// distinctes : société/facturation = cfg.co.* ; pied de page public = cfg.co.footer.*.
const FOOTER_SOCIAL_PLATFORMS = [
  "instagram",
  "facebook",
  "twitter",
  "linkedin",
  "youtube",
  "tiktok",
]

function FooterGlobalSection({ c, setC, inp }) {
  const footer = (c.co && c.co.footer) || {}
  const socials = Array.isArray(footer.social_networks) ? footer.social_networks : []

  // Helper : patch immuable de cfg.co.footer.<key> (préserve le reste de co).
  const setFooter = (key, val) =>
    setC(prev => ({
      ...prev,
      co: { ...prev.co, footer: { ...(prev.co && prev.co.footer), [key]: val } },
    }))

  const setSocial = (idx, key, val) =>
    setC(prev => {
      const prevFooter = (prev.co && prev.co.footer) || {}
      const list = Array.isArray(prevFooter.social_networks)
        ? prevFooter.social_networks
        : []
      const next = list.map((s, j) => (j === idx ? { ...s, [key]: val } : s))
      return { ...prev, co: { ...prev.co, footer: { ...prevFooter, social_networks: next } } }
    })

  const addSocial = () =>
    setC(prev => {
      const prevFooter = (prev.co && prev.co.footer) || {}
      const list = Array.isArray(prevFooter.social_networks)
        ? prevFooter.social_networks
        : []
      return {
        ...prev,
        co: {
          ...prev.co,
          footer: { ...prevFooter, social_networks: [...list, { platform: "instagram", url: "" }] },
        },
      }
    })

  const removeSocial = idx =>
    setC(prev => {
      const prevFooter = (prev.co && prev.co.footer) || {}
      const list = Array.isArray(prevFooter.social_networks)
        ? prevFooter.social_networks
        : []
      return {
        ...prev,
        co: {
          ...prev.co,
          footer: { ...prevFooter, social_networks: list.filter((_, j) => j !== idx) },
        },
      }
    })

  const sel = { border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, padding: "9px 10px", fontSize: 13, outline: "none", fontFamily: DS.fontBody, background: DS.card }

  return (
    <SubSection icon={Globe} title="Pied de page livret (global)" intro="Config unique appliquée au pied de page de tous les livrets voyageur (réseaux, copyright, mentions légales, lien site). Laisser vide = valeurs par défaut FBZ.">
      <FicheAlert tone="info" style={{ marginBottom: 14 }}>
        Le <strong>nom</strong> et l'<strong>email de contact</strong> du pied de page sont repris automatiquement de la société (ci-dessus). Le téléphone n'est pas affiché aux voyageurs.
      </FicheAlert>

      <div style={{ marginBottom: 12 }}>
        <FieldLabel>Lien site officiel</FieldLabel>
        {inp(footer.site_url || "", v => setFooter("site_url", v), "https://fullbnbzen.com")}
      </div>
      <div style={{ marginBottom: 12 }}>
        <FieldLabel>Copyright</FieldLabel>
        {inp(footer.copyright || "", v => setFooter("copyright", v), "© 2026 FULLBNBZEN SAS")}
      </div>
      <div style={{ marginBottom: 12 }}>
        <FieldLabel>Mentions légales</FieldLabel>
        <textarea
          value={footer.legal_mentions || ""}
          onChange={e => setFooter("legal_mentions", e.target.value)}
          placeholder="Société de conciergerie / mise en relation — SAS FULLBNBZEN, SIRET …"
          rows={3}
          style={{ ...daInputStyle, resize: "vertical" }}
        />
        <div style={{ fontSize: 10.5, color: DS.textMuted, marginTop: 4 }}>
          Loi Hoguet : employer « conciergerie / mise en relation / accompagnement », jamais « gestion / mandat ».
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${DS.border}`, paddingTop: 14, marginTop: 6 }}>
        <div style={{ display:"flex",alignItems:"center",gap:6,fontSize: 12, fontWeight: 700, color: DS.textSecondary, marginBottom: 10 }}>
          <Link2 size={14} strokeWidth={1.9} /> Réseaux sociaux
        </div>
        {socials.length === 0 && (
          <div style={{ fontSize: 12, color: DS.textMuted, marginBottom: 8 }}>Aucun réseau social. Ajoutez-en ci-dessous.</div>
        )}
        {socials.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select value={s.platform || "instagram"} onChange={e => setSocial(i, "platform", e.target.value)} style={sel}>
              {FOOTER_SOCIAL_PLATFORMS.map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
            <div style={{ flex: "1 1 220px" }}>
              {inp(s.url || "", v => setSocial(i, "url", v), "https://instagram.com/fullbnbzen")}
            </div>
            <button onClick={() => removeSocial(i)} className="fbz-press" style={{ background: "none", border: "none", cursor: "pointer", color: DS.textMuted, display:"inline-flex", padding:4 }} aria-label="Supprimer ce réseau"><Trash2 size={16} strokeWidth={1.8} /></button>
          </div>
        ))}
        <button onClick={addSocial} className="fbz-press" style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,width: "100%", padding: 10, background: "transparent", color: DS.marine, border: `1px dashed ${DS.borderHover}`, borderRadius: DS.radiusControl, fontSize: 12.5, fontWeight: 600, cursor: "pointer", marginTop: 4, fontFamily: DS.fontBody }}>
          <Plus size={15} strokeWidth={2} /> Ajouter un réseau social
        </button>
      </div>
    </SubSection>
  )
}
